import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card } from "@/components/ui";
import { usePushToken } from "@/hooks/usePushToken";
import { pushSupported, shouldOfferPushOptIn } from "@/lib/push";

/**
 * A tappable affordance to turn on order-status push notifications.
 *
 * The browser only shows the notification-permission prompt in response to a
 * user gesture, so it can't reliably fire as a side-effect of placing an order.
 * This card surfaces a button the customer taps directly. It self-hides when
 * push is unsupported, already granted, or denied (see shouldOfferPushOptIn).
 */
export function PushOptIn() {
  const { t } = useTranslation();
  const { enablePush, busy } = usePushToken();
  const [offer, setOffer] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      const supported = await pushSupported();
      const permission: NotificationPermission =
        typeof Notification !== "undefined" ? Notification.permission : "denied";
      if (active) setOffer(shouldOfferPushOptIn({ supported, permission }));
    })();
    return () => {
      active = false;
    };
  }, []);

  if (!offer) return null;

  const handleEnable = async () => {
    const ok = await enablePush();
    // Whether granted or dismissed, stop offering — a second gesture-less prompt
    // won't help, and a granted token is now registered.
    if (ok || (typeof Notification !== "undefined" && Notification.permission !== "default")) {
      setOffer(false);
    }
  };

  return (
    <Card padding={14} style={{ background: "#eff6ff", borderColor: "#bfdbfe" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <div style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>
          🔔
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e3a8a" }}>
            {t("v2.tracking.push_optin_title", "Get order status updates")}
          </div>
          <div style={{ fontSize: 12, color: "#1e40af", marginTop: 2, lineHeight: 1.4 }}>
            {t(
              "v2.tracking.push_optin_body",
              "Allow notifications and we'll tell you the moment your order is confirmed, prepared, and ready.",
            )}
          </div>
        </div>
        <Button variant="primary" size="sm" onClick={handleEnable} disabled={busy}>
          {busy
            ? t("v2.tracking.push_optin_enabling", "Enabling…")
            : t("v2.tracking.push_optin_cta", "Turn on")}
        </Button>
      </div>
    </Card>
  );
}
